//core
import React, {useEffect, useState, useCallback} from "react";
import arePropsEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isValid } from "date-fns";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { FormControl, Box, InputAdornment, IconButton } from "@mui/material";

//locales
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import trLocale from "date-fns/locale/tr";
import koLocale from "date-fns/locale/ko";
import elLocale from "date-fns/locale/el";
import frLocale from "date-fns/locale/fr";
import ptLocale from "date-fns/locale/pt";
import thLocale from "date-fns/locale/th";
import viLocale from "date-fns/locale/vi";
import idLocale from "date-fns/locale/id";
import zhLocale from "date-fns/locale/zh-CN";

//styles
import { useStyles } from "./styles/styles";
import Calendar from "@mui/icons-material/Event";

//types
interface IMuiFieldProps {
  id?: string;
  name: string;
  autoComplete?: string;
  label: string;
  value?: any;
  secondValue?: any;
  maxDate?: any;
  minDate?: any;
  hideError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  paperClass?: string;
  helperText?: string;
  dataId?: string;
  InputProps?: any;
  error?: boolean | string;
  validation?: (value: any) => any;
  onChange: (value: Date) => any;
  noMB?: boolean;
  extraSmall?: boolean;
  onCloseCallback?: () => void;
  views?: any;
  inputFormat?: string;
  disablePortal?: boolean;
  validateDates?: boolean;
  onValidationError?: (error: string, date: any) => void;
}

const localeMap = {
  en: enLocale,
  ru: ruLocale,
  uk: ukLocale,
  es: esLocale,
  tr: trLocale,
  ko: koLocale,
  el: elLocale,
  fr: frLocale,
  pt: ptLocale,
	th: thLocale,
	vi: viLocale,
	id: idLocale,
  zh: zhLocale,
};

const CSingleDatePicker = (props: IMuiFieldProps) => {
  const { i18n } = useTranslation();
  const styles = useStyles();

  const {
    id,
    name,
    autoComplete,
    label,
    value,
    secondValue,
    disabled = false,
    maxDate,
    minDate,
    error = '',
    hideError = false,
    className = '',
    paperClass = '',
    noMB = false,
    extraSmall = false,
    inputFormat = 'dd/MM/yyyy',
    onChange,
    onCloseCallback,
    views = ['year', 'month', 'day'],
    disablePortal = true,
    validateDates = true,
    onValidationError,
  } = props;

  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState<object>(localeMap.en);

  const [dateValue, setDateValue] = useState<Date | null>(() => {
    if (!value || value === '' || value === null || value === undefined) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
  });

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(localeMap.ru);
    }
    if (i18n.language === 'en') {
      setLocale(localeMap.en);
    }
    if (i18n.language === 'uk') {
      setLocale(localeMap.uk);
    }
    if (i18n.language === 'es') {
      setLocale(localeMap.es);
    }
    if (i18n.language === 'tr') {
      setLocale(localeMap.tr);
    }
    if (i18n.language === 'ko') {
      setLocale(localeMap.ko);
    }
    if (i18n.language === 'el') {
      setLocale(localeMap.el);
    }
    if (i18n.language === 'fr') {
      setLocale(localeMap.fr);
    }
    if (i18n.language === 'pt') {
      setLocale(localeMap.pt);
    }
	  if (i18n.language === 'th') {
		  setLocale(localeMap.th);
	  }
	  if (i18n.language === 'vi') {
		  setLocale(localeMap.vi);
	  }
	  if (i18n.language === 'id') {
		  setLocale(localeMap.id);
	  }
    if (i18n.language === 'zh') {
      setLocale(localeMap.zh);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (!value || value === '' || value === null || value === undefined) {
      setDateValue(null);
      return;
    }
    const date = new Date(value);
    setDateValue(isValid(date) ? date : null);
  }, [value]);

  const validateDate = (date: Date | null): { isValid: boolean; error?: string } => {
    if (!validateDates) {
      return { isValid: true };
    }

    if (!date) {
      return { isValid: false, error: 'Date is required' };
    }

    if (!isValid(date)) {
      return { isValid: false, error: 'Invalid date provided' };
    }

    if (minDate && date < new Date(minDate)) {
      return { isValid: false, error: 'Date is before minimum allowed date' };
    }

    if (maxDate && date > new Date(maxDate)) {
      return { isValid: false, error: 'Date is after maximum allowed date' };
    }

    return { isValid: true };
  };

  const handleChange = useCallback((newValue: Date | null) => {
    setDateValue(newValue);

    if (newValue && isValid(newValue)) {
      const validation = validateDate(newValue);

      if (!validation.isValid) {
        console.error('Date validation failed:', validation.error);
        if (onValidationError) {
          onValidationError(validation.error!, newValue);
        }
        return;
      }

      // Call onChange when date is valid
      onChange(newValue);
    }
  }, [onChange, validateDates, minDate, maxDate, onValidationError]);

  const TextField = React.forwardRef(
    (props: any, ref: React.Ref<HTMLDivElement>) => (
      <TextField
        id={id}
        name={name}
        className={`${styles.input} ${extraSmall && styles.small}`}
        autoComplete={autoComplete}
        size="small"
        {...props}
        {...(hideError ? {error: false} : {})}
      />
    ),
  );

  return (
    <LocalizationProvider
      //@ts-ignore
      dateAdapter={AdapterDateFns} locale={locale}>
      <Box className={styles.formField} sx={{ marginBottom: noMB ? '0' : '24px' }}>
        <FormControl fullWidth>
          <DesktopDatePicker
            className={className}
            open={disabled ? false : open}
            onOpen={() => setOpen(true)}
            onClose={() => {
              setOpen(false);
              if (typeof onCloseCallback === 'function') {
                onCloseCallback();
              }
            }}
            label={label}
            disabled={disabled}
            value={new Date(value)}
            format={inputFormat}
            {...(maxDate ? {maxDate: new Date(maxDate)} : {})}
            {...(minDate ? {minDate: new Date(minDate)} : {})}
            {...(views ? {views: views} : {})}
            // @ts-ignore
            onChange={handleChange}
            slotProps={{
              popper: {
                disablePortal: disablePortal
              },
              layout: {
                sx: {
                  paperClass
                },
              },
              textField: {
                size: 'small',
                InputProps: {
                  endAdornment: <InputAdornment position="end">
                    <IconButton
                      size="small"
                      className={styles.iconButton}
                      sx={{ padding: '2px' }}
                      onClick={() => setOpen(!open)}
                    >
                      <Calendar color="action" />
                    </IconButton>
                  </InputAdornment>,
                },
              },
              actionBar: ({ wrapperVariant }) => {
                return {
                  actions: wrapperVariant === 'desktop' ? [] : ['accept'],
                }
              },
            }}
          />
          {/*{ meta.error && meta.error !== 'error' ? (
            <FormHelperText error>{meta.error}</FormHelperText>
          ) : null }*/}
        </FormControl>
      </Box>
    </LocalizationProvider>
  );
};

export default React.memo(CSingleDatePicker, arePropsEqual);
